import * as amplitude from '@amplitude/analytics-browser';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { client, fireStoreClient } from 'api/baseApi.ts';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { AuthenticationStatus } from 'core/constants/enums/AuthenticationStatus.ts';
import { AuthProcess } from 'core/constants/enums/AuthProcess.ts';
import { FirebaseContext } from 'core/hooks/firebase-context/firebaseContext';
import BlockedState from 'core/navigation/blocked-state/BlockedState';
import {
	maintenanceBannerSelector,
	marketingBannerWorkingSelector,
	serverWorkingSelector,
	setCurrentUser,
	setGetCurrentUserAPIStage,
	setIsAppUpdateAvailable,
	setMaintenanceBanner,
	setMarketingBannerWorkingWorking,
	setPublicCanvasWorking,
	setSaleData,
	setServerWorking,
	setUserAuthenticationStatus,
	userBlockedSelector,
	userProfileSelector,
} from 'core/store/reducers/appReducer/AppSlice';
import {
	fetchAndVerifyAuthToken,
	setCurrentUserThunk,
} from 'core/store/reducers/appReducer/appSliceThunk';
import { captureDatadogErrors } from 'core/utils/datadogUtil';
import { getCurrentUser } from 'core/utils/firebaseUtil';
import posthog from 'posthog-js';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import semver from 'semver/preload';

const db = client.getFirebaseDb();
const { doc: fireStoreDoc, onSnapshot } = fireStoreClient;

function InitialiseUser() {
	const dispatch = useDispatch();

	const serverWorking = useSelector(serverWorkingSelector);
	const userBlocked = useSelector(userBlockedSelector);
	const marketingBannerWorking = useSelector(marketingBannerWorkingSelector);
	const maintenanceBanner = useSelector(maintenanceBannerSelector);
	const userProfile = useSelector(userProfileSelector);
	const userProfileCreatedAtRef = useRef(userProfile?.createdAt);
	useEffect(() => {
		userProfileCreatedAtRef.current = userProfile?.createdAt;
	}, [userProfile?.createdAt]);

	useEffect(() => {
		// CHECKING SERVER STATUS
		const serverStatusUnsubscribe = onSnapshot(fireStoreDoc(db, 'server', 'status'), (doc) => {
			const data = doc?.data();
			if (data) {
				const { working = true, testWorking = true, publicCanvasWorking = true } = data;

				const updatedServerWorking = process.env.NODE_ENV !== 'production' ? testWorking : working;

				if (updatedServerWorking !== undefined) {
					if (serverWorking !== updatedServerWorking) {
						dispatch(setServerWorking(updatedServerWorking));
					}
				}

				if (publicCanvasWorking != null) {
					dispatch(setPublicCanvasWorking(publicCanvasWorking));
				}
			}
		});
		return () => {
			serverStatusUnsubscribe();
		};
	}, [dispatch, serverWorking]);

	useEffect(() => {
		// CHECKING MARKETING BANNER STATUS
		const marketingBannerStatusUnsubscribe = onSnapshot(
			fireStoreDoc(db, 'marketingBanner', 'status'),
			(doc) => {
				const data = doc?.data();
				if (data) {
					const { working = false, testWorking = false } = data;

					const updatedBannerWorking =
						process.env.NODE_ENV !== 'production' ? testWorking : working;

					if (updatedBannerWorking !== undefined) {
						if (marketingBannerWorking !== updatedBannerWorking) {
							dispatch(setMarketingBannerWorkingWorking(updatedBannerWorking));
						}
					}
				}
			},
		);
		return () => {
			marketingBannerStatusUnsubscribe();
		};
	}, [dispatch, marketingBannerWorking]);

	useEffect(() => {
		// GETTING MAINTENANCE STATUS
		const maintenanceBannerStatusUnsubscribe = onSnapshot(
			fireStoreDoc(db, 'maintenanceBanner', 'data'),
			(doc) => {
				const data = doc?.data();
				if (data) {
					const { status = false } = data;

					if (maintenanceBanner?.status !== status) {
						dispatch(setMaintenanceBanner(data));
					}
				}
			},
		);
		return () => {
			maintenanceBannerStatusUnsubscribe();
		};
	}, [dispatch, maintenanceBanner?.status]);

	useEffect(() => {
		// GET LATEST APP VERSION
		const appDataVersionUnsubscribe = onSnapshot(
			fireStoreDoc(db, 'appData', 'latestVersion'),
			(doc) => {
				const data = doc?.data();
				if (data) {
					const { version } = data;
					const localVersion = process.env.REACT_APP_VERSION;
					const isServerVersionGreater = semver.gt(version, localVersion);

					if (isServerVersionGreater) {
						dispatch(setIsAppUpdateAvailable(true));
					}
				}
			},
		);
		return () => {
			appDataVersionUnsubscribe();
		};
	}, [dispatch]);

	useEffect(() => {
		// GET SALE DATA
		const appDataSaleUnsubscribe = onSnapshot(fireStoreDoc(db, 'appData', 'sale'), (doc) => {
			const docData = doc?.data();
			if (docData && docData.data) {
				dispatch(setSaleData(docData.data));
			}
		});

		return () => {
			appDataSaleUnsubscribe();
		};
	}, [dispatch]);

	useEffect(() => {
		getCurrentUser()
			.then((user) => {
				if (user) {
					dispatch(setCurrentUserThunk(user));
					if (!user.isAnonymous) {
						posthog.identify(user.email, {
							userPropertiesToSetOnce: {
								createdAt: userProfileCreatedAtRef.current,
							},
						}); // posthog.identify
						amplitude.setUserId(user.email); // Set Amplitude User ID
						const identify = new amplitude.Identify();
						identify.setOnce('createdAt', userProfileCreatedAtRef.current);
						amplitude.identify(identify);
						datadogRum.setUser({
							email: user.email,
						}); // Set Datadog RUM User ID
						datadogLogs.setUser({
							email: user.email,
						}); // Set Datadog Logs User ID
						window.clarity('identify', user.email);
						dispatch(fetchAndVerifyAuthToken(user, AuthProcess.INITIALISE_USER));
					}
				} else {
					dispatch(setCurrentUser(null));
				}
			})
			.catch((error) => {
				captureDatadogErrors('getCurrentUser not found in InitialisedUser', {}, error);
				dispatch(setCurrentUser(null));
				dispatch(setUserAuthenticationStatus(AuthenticationStatus.UNAUTHENTICATED));
			})
			.finally(() => {
				dispatch(setGetCurrentUserAPIStage(ApiStage.SUCCESS));
			});
	}, [dispatch]);

	if (userBlocked) {
		return <BlockedState />;
	}

	return (
		<FirebaseContext>
			<Outlet />
		</FirebaseContext>
	);
}

export default InitialiseUser;
